import clsx from 'clsx';
import React, { memo, useCallback, useId, useMemo, useState } from 'react';
import { DataGridLightCell } from '../data-grid-cell';
import { useDataGridLightSettings } from '../../hooks';
import { DataGridLightCardMode, DataGridLightColumn, DataGridLightRowProps } from '../../models';
import { CardDetailsToggle } from 'components/card-details-toggle';
import style from './index.module.scss';
import { hasCellValue, isRowIdColumn, isTextValued } from 'components/card-anatomy';

/**
 * The card list `DataGridLight` renders below `md` when the instance opted in
 * (Phase F1, D-10 / D-53). Every value is the table's own cell
 * (`DataGridLightCell` as a `div`: `renderColumn`, the editor container, the
 * cell props); every label is the column's `renderHeader`. Rows keep their
 * `rowProps` on the card. Sticky columns have no meaning here.
 *
 * N1 (D-92): the heavy grid's card anatomy -- the head (the title line and the
 * line under it, R5 labels), the face (label / value rows), the foot (the
 * "details" expand at the start, the `more` rows opening above it; the card's
 * action at the end).
 */
export interface CardLayout {
  primary: DataGridLightColumn<any, any> | null;
  secondary: DataGridLightColumn<any, any> | null;
  fields: DataGridLightColumn<any, any>[];
  /** N1: the rows behind the "details" expand. */
  more: DataGridLightColumn<any, any>[];
  actions: DataGridLightColumn<any, any>[];
  headLabels: Set<'primary' | 'secondary'>;
}

/** N2b (PD-10): the automatic anatomy's face rows, as on the heavy grid's cards. */
export const AUTO_FACE_ROWS = 4;

const isControls = (column: DataGridLightColumn<any, any>) => column.field === 'CONTROLS';
/** A row-level control column the grid adds (`__checked`: the row's select box). */
const isRowControl = (column: DataGridLightColumn<any, any>) =>
  isControls(column) || String(column.field).startsWith('__');
const isEditable = (column: DataGridLightColumn<any, any>) => Boolean(column.editable);

/**
 * N2b (PD-10): the anatomy of a grid that declares no `cardMode` -- the title
 * is (N2c, SC-N2b-09) the first content column that is not an editable cell
 * and whose rows hold text, so never an id, a number or a date while such a
 * column exists; else the first content column that is not an editable cell
 * (else the first content column). The next AUTO_FACE_ROWS are label / value
 * rows, the rest are behind "details" -- the row's index / id among them,
 * never on the face (N2c); the row-level controls (`CONTROLS`, a `__` select
 * box) are the card's actions.
 */
const resolveAutoLayout = (columns: DataGridLightColumn<any, any>[], rows?: any[]): CardLayout => {
  const content = columns.filter((column) => !isRowControl(column));
  const holdsText = (column: DataGridLightColumn<any, any>) =>
    !isEditable(column) &&
    !isRowIdColumn(column.field) &&
    isTextValued(rows, (row) => row?.[column.field]);
  const primary =
    content.find(holdsText) || content.find((column) => !isEditable(column)) || content[0] || null;
  const rest = content.filter((column) => column !== primary);
  const face = rest.filter((column) => !isRowIdColumn(column.field)).slice(0, AUTO_FACE_ROWS);
  return {
    primary,
    secondary: null,
    fields: face,
    more: rest.filter((column) => !face.includes(column)),
    actions: columns.filter(isRowControl),
    headLabels: new Set(),
  };
};

export const resolveCardLayout = (
  columns: DataGridLightColumn<any, any>[],
  cardMode: DataGridLightCardMode | undefined,
  rows?: any[],
): CardLayout => {
  if (!cardMode) {
    return resolveAutoLayout(columns, rows);
  }
  const byId = (id: string | undefined) =>
    id ? columns.find((column) => column.id === id) || null : null;
  const listed = (ids: string[]) =>
    ids.map(byId).filter((column): column is DataGridLightColumn<any, any> => !!column);
  const primary = byId(cardMode.primary) || columns[0] || null;
  const secondary = byId(cardMode.secondary);
  const actions = listed(cardMode.actions ?? ['CONTROLS']);
  const onFace = (column: DataGridLightColumn<any, any>) =>
    column !== primary && column !== secondary && !actions.includes(column);
  // N2c (T4): the row's index / id is behind "details" on a declared card too
  const rowIds = (cardMode.fields ? listed(cardMode.fields) : columns).filter(
    (column) => onFace(column) && isRowIdColumn(column.field),
  );
  const more = [
    ...rowIds.filter((column) => !(cardMode.more ?? []).includes(column.id)),
    ...listed(cardMode.more ?? []).filter(onFace),
  ];
  const fields = (cardMode.fields ? listed(cardMode.fields) : columns).filter(
    (column) => onFace(column) && !more.includes(column),
  );
  // N1 (D-92): no inline labels by default, as on the heavy grid's cards
  const headLabels = new Set<'primary' | 'secondary'>(cardMode.headLabels ?? []);
  return { primary, secondary, fields, more, actions, headLabels };
};

const hasValue = (row: any, column: DataGridLightColumn<any, any>) =>
  hasCellValue(row?.[column.field]);

interface CardProps {
  row: any;
  layout: CardLayout;
  rowProps?: DataGridLightRowProps<any>;
}

const DataGridLightCard = memo<CardProps>(({ row, layout, rowProps }) => {
  const { classes } = useDataGridLightSettings();
  const moreId = useId();
  const [expanded, setExpanded] = useState(false);
  const onToggle = useCallback(() => setExpanded((value) => !value), []);
  const RowProps = useMemo(
    () =>
      (typeof rowProps === 'function' ? rowProps(row) : rowProps) as
        | React.HTMLAttributes<HTMLElement>
        | undefined,
    [row, rowProps],
  );
  const { primary, secondary, actions, headLabels } = layout;
  // N1: the details toggle only on a row with a detail to show (rubric 6)
  const more = useMemo(
    () => (layout.more.some((column) => hasValue(row, column)) ? layout.more : []),
    [layout.more, row],
  );
  // N3 (T2): a face row with no value is left off the card; the column stays
  // in the details
  const face = useMemo(
    () => layout.fields.filter((column) => hasValue(row, column)),
    [layout.fields, row],
  );
  const headLabel = (column: DataGridLightColumn<any, any>, cell: 'primary' | 'secondary') =>
    headLabels.has(cell) ? (
      <span className={style.headLabel}>{column.renderHeader({ field: column.field })}</span>
    ) : null;
  const field = (column: DataGridLightColumn<any, any>) => (
    <div className={style.field} key={column.id}>
      <dt className={style.label}>{column.renderHeader({ field: column.field })}</dt>
      <dd className={style.value}>
        <DataGridLightCell column={column} row={row} component="div" />
      </dd>
    </div>
  );
  return (
    <div {...RowProps} className={clsx(style.card, classes.row, RowProps?.className)}>
      <div className={style.head}>
        <div className={style.titles}>
          {primary && (
            <div className={style.primary}>
              {headLabel(primary, 'primary')}
              <DataGridLightCell column={primary} row={row} component="div" />
            </div>
          )}
          {secondary && (
            <div className={style.secondary}>
              {headLabel(secondary, 'secondary')}
              <DataGridLightCell column={secondary} row={row} component="div" />
            </div>
          )}
        </div>
      </div>
      {face.length > 0 && <dl className={style.fields}>{face.map(field)}</dl>}
      {expanded && (
        <dl className={style.fields} id={moreId}>
          {more.map(field)}
        </dl>
      )}
      {(more.length > 0 || actions.length > 0) && (
        <div className={style.foot}>
          {more.length > 0 && (
            <CardDetailsToggle
              className={style.toggle}
              expanded={expanded}
              onToggle={onToggle}
              controls={moreId}
            />
          )}
          {actions.length > 0 && (
            <div className={style.actions}>
              {actions.map((column) => (
                <DataGridLightCell key={column.id} column={column} row={row} component="div" />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
});

interface Props {
  /** Undefined = the automatic anatomy (N2b). */
  cardMode?: DataGridLightCardMode;
  columns: DataGridLightColumn<any, any>[];
  rows: any[];
  rowProps?: DataGridLightRowProps<any>;
  rowKey?: any;
}

export const DataGridLightCards = memo<Props>(({ cardMode, columns, rows, rowProps, rowKey }) => {
  // N2c (T4): the automatic title reads the rows' values (never an id, a number or a date)
  const layout = useMemo(
    () => resolveCardLayout(columns, cardMode, rows),
    [columns, cardMode, rows],
  );
  return (
    <div className={style.list}>
      {rows.map((row, i) => {
        const key = rowKey ? row[rowKey] : i;
        return <DataGridLightCard key={key} row={row} layout={layout} rowProps={rowProps} />;
      })}
    </div>
  );
});
